import { useEffect, useRef } from 'react';
import { withWindow } from '@shared/utils/withWindow';
import { useUnlockDialogContext } from './UnlockDialog.context';

/**
 * Hook that defers input focus after drawer animation completes.
 *
 * iOS Safari/Chrome doesn't trigger native keyboard scroll behavior for synchronous
 * programmatic focus (like `enableFocusOnMount`). By deferring the focus with
 * a delay after the drawer animation, iOS treats it more like a user-initiated focus
 * and correctly scrolls the input into view above the keyboard.
 *
 * Uses the dialog's isOpen state to re-trigger focus each time the dialog opens.
 * Pass `focusOnMount: true` when rendering outside a dialog (e.g. a full-page gate)
 * where isOpen is never set — focus will fire once on mount instead.
 */
export const useDeferredInputFocus = <T extends HTMLElement = HTMLInputElement>(options?: { focusOnMount?: boolean }) => {
  const ref = useRef<T>(null);
  const { isOpen } = useUnlockDialogContext();
  const focusOnMount = options?.focusOnMount ?? false;

  useEffect(() => {
    if (!focusOnMount && !isOpen) return;

    // Wait for drawer animation to complete before focusing
    const timer = withWindow(
      global =>
        global.setTimeout(() => {
          ref.current?.focus();
        }, 300),
      undefined,
    );
    return () => withWindow(global => global.clearTimeout(timer));
  }, [isOpen, focusOnMount]);

  return ref;
};
